import { useEffect, useRef, type ReactNode } from "react";
import { useLenis } from "../providers/LenisProvider";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

type VelocitySkewProps = {
  children: ReactNode;
  maxSkewDeg?: number;
  className?: string;
};

export function VelocitySkew({ children, maxSkewDeg = 2, className }: VelocitySkewProps) {
  const ref = useRef<HTMLDivElement>(null);
  const lenis = useLenis();
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    // Only run with Lenis (desktop). On mobile, native scroll is already smooth
    // and the rAF loop creates GPU compositing conflicts with RevealLines'
    // overflow:hidden clips on iOS Safari, breaking text entrance animations.
    if (reducedMotion || !lenis) return;

    let currentSkew = 0;
    let targetSkew = 0;
    let raf: number;

    const onScroll = ({ velocity }: { velocity: number }) => {
      targetSkew = Math.max(-1, Math.min(1, velocity / 30)) * maxSkewDeg;
    };
    lenis.on("scroll", onScroll);

    function loop() {
      targetSkew *= 0.92;
      currentSkew += (targetSkew - currentSkew) * 0.12;
      if (ref.current) {
        ref.current.style.transform = `skewY(${currentSkew.toFixed(3)}deg)`;
      }
      raf = requestAnimationFrame(loop);
    }
    raf = requestAnimationFrame(loop);

    return () => {
      lenis.off("scroll", onScroll);
      cancelAnimationFrame(raf);
      if (ref.current) ref.current.style.transform = "";
    };
  }, [lenis, reducedMotion, maxSkewDeg]);

  // willChange only when the skew effect is actually active (Lenis on desktop)
  const active = !!lenis && !reducedMotion;

  return (
    <div ref={ref} className={className} style={active ? { willChange: "transform" } : undefined}>
      {children}
    </div>
  );
}
